import Client from "@searchkit/instantsearch-client";
import isEqual from "fast-deep-equal/es6";
import { BaseSyntheticEvent, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { create } from "zustand";
import { config } from "./config";
import { useSelection } from "./selection";
import { GroupedCodesDTO, Tag, TagColor, User } from "./types";
import { fetchState, FetchState } from "./util";

export const useNavigateStart = () => {
  const clear = useSelection((c) => c.clear);
  const navigate = useNavigate();
  return useCallback(
    (e?: BaseSyntheticEvent) => {
      clear();
      navigate("/");
      e?.stopPropagation();
    },
    [clear, navigate]
  );
};

export const useSection = (): string | undefined =>
  /\/location\/[\d,.]+\/?(.*)/.exec(useLocation().pathname)?.[1];
export const useIsSection = (section: string) => useSection() === section;

// Kortstondige meldingen ("Link gekopieerd!") — render via <Toasts/> in Main
interface Toast {
  id: number;
  text: string;
}
interface ToastState {
  toasts: Toast[];
  show: (text: string, ms?: number) => void;
  dismiss: (id: number) => void;
}
let toastId = 0;
export const useToast = create<ToastState>((set, get) => ({
  toasts: [],
  show: (text, ms = 3000) => {
    const id = ++toastId;
    set({ toasts: [...get().toasts, { id, text }] });
    setTimeout(() => get().dismiss(id), ms);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

// Welke labels de kavelkaart op hoog zoomniveau toont: kadastrale
// aanduidingen (label-laag) of huisnummers (housenr-laag uit de pmtiles)
export type MapLabelMode = "cadastre" | "housenr";
interface MapLabelState {
  mode: MapLabelMode;
  setMode: (mode: MapLabelMode) => void;
}
export const useMapLabels = create<MapLabelState>((set) => ({
  mode: "cadastre",
  setMode: (mode) => set({ mode }),
}));

// Generic UI-selection store used to coordinate highlighting between the report
// and the map. `kind` names what type of thing is selected (e.g. "building"),
// `args` carries whatever the click provided (e.g. a BAG-id). Map/report
// components subscribe to a single `kind` via useSelectionFor() and only react
// when that kind is active.
interface Selected {
  kind: string;
  args?: any;
}
interface SelectedState {
  selected?: Selected;
  select: (kind: string, args?: any) => void;
  toggle: (kind: string, args?: any) => void;
  clear: (kind?: string) => void;
}
export const useSelected = create<SelectedState>((set, get) => ({
  select: (kind, args) => set({ selected: { kind, args } }),
  toggle: (kind, args) => {
    const cur = get().selected;
    const same = cur?.kind === kind && isEqual(cur.args, args);
    set({ selected: same ? undefined : { kind, args } });
  },
  // clear everything, or only when the active selection matches `kind`
  clear: (kind) => set((s) => (!kind || s.selected?.kind === kind ? { selected: undefined } : s)),
}));

// Subscribe to the current selection of a single `kind`. Returns the selection
// args when `kind` is active, otherwise undefined — so a component wired to one
// kind never re-renders for selections of another kind.
export const useSelectionFor = <T = any>(kind: string): T | undefined =>
  useSelected((s) => (s.selected?.kind === kind ? (s.selected.args as T) : undefined));

interface AccountState {
  state?: FetchState;
  user?: User;
  name: () => string | undefined;
  logout: () => void;
  refresh: () => void;
}

export const useAccount = create<AccountState>((set, get) => ({
  refresh: async () => {
    if (get().state == "fetching") return;
    fetchState("/api/user", ({ state, data }) => {
      set({ state, user: data });
      if (state === "fetched" && data?.username) {
        useProjects.getState().fetchProjects(true);
        useProjects.getState().fetchTags();
      }
    });
  },
  name: () => {
    const user = get().user;
    if (!user) return;
    return user.firstName ? `${user.firstName} ${user.lastName}` : user.username;
  },
  logout: async () => {
    set({ state: "fetched", user: undefined });
    useProjects.setState({
      state: undefined,
      projects: undefined,
      selectedProjectId: null,
      tags: undefined,
    });
  },
}));

type SearchState = {
  client: any;
  filter: any;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  setFilter: (filter: any) => void;
};
export const useSearch = create<SearchState>((set, get) => {
  const skl = {
    // the url of your instantsearch-elasticsearch-adapter API endpoint
    url: `${config.searchUrl}/api/search`,
  };
  const original = Client(skl);
  const client = {
    ...original,
    search: async (s) => {
      const r = {};
      for (const [key, value] of Object.entries(s[0].params)) {
        if (/query|filter/i.test(key)) {
          r[key] = value;
        }
      }
      if (!isEqual(get().filter, r)) set({ filter: r });
      return original.search(s);
    },
    // Client() geeft een class-instance terug; object-spread kopieert geen
    // prototype-methoden. Zonder deze forward mist instantsearch
    // searchForFacetValues en valt het terug op client.search zónder
    // facetName — searchkit slaat de facetQuery-filtering dan over
    // (RefinementList searchable leek te werken maar filterde niet).
    searchForFacetValues: (...args: any[]) => (original as any).searchForFacetValues(...args),
  };
  return {
    client,
    filter: null,
    visible: false,
    setVisible: (visible: boolean) => set({ visible }),
    setFilter: (filter: any) => set({ filter }),
  };
});

export interface SourceLayer {
  name: string;
  type: "vector" | "raster";
  title: string;
  url: string;
  group: string;
  icon: string;
  labels?: boolean;
  attributes?: any;
}

// Layers selection store: used by Layers panel and map component
type LayerState = {
  state?: FetchState;
  layers?: SourceLayer[];
  fetchLayers: () => void;

  backgroundId: string;
  selected: SourceLayer[];
  opacities: Record<string, number>;
  toggle: (layer: SourceLayer) => void;
  move: (layer: SourceLayer, direction: 1 | -1) => void;
  setAll: (layers: SourceLayer[]) => void;
  setBackground: (id: string) => void;
  setOpacity: (id: string, opacity: number) => void;
  clear: () => void;
  isSelected: (id: string) => boolean;
};

export const useLayers = create<LayerState>((set, get) => ({
  fetchLayers: () => {
    const { state } = get();
    if (!state)
      fetchState(`${config.referenceUrl}/api/layers`, ({ state, data }) =>
        set({ state, layers: data })
      );
  },
  backgroundId: "default",
  selected: [],
  opacities: {},
  toggle: (layer: SourceLayer) => {
    const cur = get().selected;
    const has = cur.includes(layer);
    const next = has ? cur.filter((x) => x !== layer) : [...cur, layer];
    set({ selected: next });
  },
  // Volgorde in `selected` is de stapelvolgorde op de kaart (laatste = boven)
  move: (layer: SourceLayer, direction: 1 | -1) => {
    const next = [...get().selected];
    const i = next.indexOf(layer);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set({ selected: next });
  },
  setAll: (layers: SourceLayer[]) => set({ selected: Array.from(new Set(layers)) }),
  setBackground: (id) => set({ backgroundId: id }),
  setOpacity: (id, opacity) => set({ opacities: { ...get().opacities, [id]: opacity } }),
  clear: () => set({ selected: [], backgroundId: "default", opacities: {} }),
  isSelected: (id: string) => !!get().selected.find((l) => l.name === id),
}));

interface CodeState {
  codesPerLayer: Record<string, GroupedCodesDTO[]>;
  fetchMissingCodes: (layers: SourceLayer[]) => void;
}
export const useCode = create<CodeState>((set, get) => ({
  codesPerLayer: {}, // cache
  fetchMissingCodes: async (layers: SourceLayer[]) => {
    const { codesPerLayer } = get();
    const missing = layers.filter(
      (layer) => !layer.labels && !codesPerLayer.hasOwnProperty(layer.name)
    );
    if (!missing.length) return;
    const update: Record<string, any> = { ...codesPerLayer };
    for (const layer of missing) {
      if (layer.labels) continue;
      await fetch(`${config.referenceUrl}/api/codes/${layer.name}/groups`)
        .then((r) => r.json())
        .then((data) => {
          update[layer.name] = data;
        });
    }
    set({ codesPerLayer: update });
  },
}));
interface ParcelPayload {
  name: string;
  geometry: any;
  center: any;
  description?: string;
}

interface ProjectsState {
  state?: FetchState;
  projects?: any[];
  selectedProjectId: number | null;
  tags?: Tag[];
  fetchProjects: (force?: boolean) => void;
  createProject: (name: string) => Promise<any | null>;
  updateProject: (id: number, name: string) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  selectProject: (id: number | null) => void;
  addParcelToProject: (projectId: number, parcel: ParcelPayload) => Promise<boolean>;
  removeParcelFromProject: (projectId: number, parcelId: number) => Promise<void>;
  fetchTags: () => Promise<void>;
  createTag: (name: string, color: TagColor) => Promise<Tag | null>;
  updateTag: (id: number, name: string, color: TagColor) => Promise<Tag | null>;
  deleteTag: (id: number) => Promise<void>;
  setParcelTags: (projectId: number, parcelId: number, tagIds: number[]) => Promise<void>;
}
export const useProjects = create<ProjectsState>((set, get) => ({
  selectedProjectId: null,
  fetchProjects: async (force?: boolean) => {
    if (get().state && !force) return;
    // Stale-while-revalidate: tijdens een refetch de bestaande lijst laten
    // staan (data is dan nog undefined) — anders flitst de hele UI leeg
    fetchState("/api/projects", ({ state, data }) =>
      set(data !== undefined ? { state, projects: data } : { state })
    );
  },
  createProject: async (name: string) => {
    const response = await fetch("/api/projects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) return null;
    const project = await response.json();
    get().fetchProjects(true);
    return project;
  },
  updateProject: async (id: number, name: string) => {
    const response = await fetch(`/api/projects/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (response.ok) {
      get().fetchProjects(true);
    }
  },
  deleteProject: async (id: number) => {
    const response = await fetch(`/api/projects/${id}`, {
      method: "DELETE",
    });
    if (response.ok) {
      get().fetchProjects(true);
    }
  },
  selectProject: (id: number | null) => set({ selectedProjectId: id }),
  addParcelToProject: async (projectId: number, parcel: ParcelPayload) => {
    const response = await fetch(`/api/projects/${projectId}/parcels`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(parcel),
    });
    if (response.ok) {
      get().fetchProjects(true);
      return true;
    }
    return false;
  },
  removeParcelFromProject: async (projectId: number, parcelId: number) => {
    const response = await fetch(`/api/projects/${projectId}/parcels/${parcelId}`, {
      method: "DELETE",
    });
    if (response.ok) {
      get().fetchProjects(true);
    }
  },
  fetchTags: async () => {
    const response = await fetch("/api/tags");
    if (!response.ok) return;
    const tags = (await response.json()) as Tag[];
    set({ tags });
  },
  createTag: async (name: string, color: TagColor) => {
    const response = await fetch("/api/tags", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, color }),
    });
    if (!response.ok) return null;
    const tag = (await response.json()) as Tag;
    set({ tags: [...(get().tags ?? []), tag].sort((a, b) => a.name.localeCompare(b.name)) });
    return tag;
  },
  updateTag: async (id: number, name: string, color: TagColor) => {
    const response = await fetch(`/api/tags/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, color }),
    });
    if (!response.ok) return null;
    const tag = (await response.json()) as Tag;
    set({
      tags: (get().tags ?? [])
        .map((t) => (t.id === id ? tag : t))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
    // Tag rename/recolor affects parcels rendered with this tag
    get().fetchProjects(true);
    return tag;
  },
  deleteTag: async (id: number) => {
    const response = await fetch(`/api/tags/${id}`, { method: "DELETE" });
    if (!response.ok) return;
    set({ tags: (get().tags ?? []).filter((t) => t.id !== id) });
    get().fetchProjects(true);
  },
  setParcelTags: async (projectId: number, parcelId: number, tagIds: number[]) => {
    // Optimistic local update so reorder feels instant
    const tagsById = new Map((get().tags ?? []).map((t) => [t.id, t]));
    const newParcelTags = tagIds.map((id) => tagsById.get(id)).filter(Boolean) as Tag[];
    set((s) => ({
      projects: (s.projects ?? []).map((p) =>
        p.id !== projectId
          ? p
          : {
              ...p,
              parcels: (p.parcels ?? []).map((pc: any) =>
                pc.id === parcelId ? { ...pc, tags: newParcelTags } : pc
              ),
            }
      ),
    }));
    const response = await fetch(`/api/projects/${projectId}/parcels/${parcelId}/tags`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tagIds }),
    });
    if (!response.ok) {
      get().fetchProjects(true); // reconcile if backend rejected
    }
  },
}));
